import React, { useEffect, useState } from "react";
import styles from "./Inbox.module.css";
import AxiosInstance, {
  TokenService,
} from "../../../services/Api";
import BlobImage from "../../../components/BlobImage/BlobImage";
import { useSelector } from "react-redux";
import moment from "moment";
import { CustomTextInput } from "../../../components";

const SendItems = () => {
  const user = TokenService.getUser();
  const userId = user?.id || 0;
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const { translations } = useSelector((state) => state.language);

  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchSentMessages();
  }, []);

  const fetchSentMessages = async (search = "") => {
    try {
      setLoading(true);
      const payload = {
        school_id: school_id ? parseInt(school_id) : 0,
        user_id: parseInt(userId),
        search: search,
      };
      const response = await AxiosInstance.post(
        "/message/sentItemList",
        payload,
      );

      if (response.data?.status && Array.isArray(response.data.data)) {
        const formatted = response.data.data.map((msg) => ({
          id: msg.id,
          subject: msg.subject || "",
          body: msg.messages || "",
          senderInitial: msg.subject?.charAt(0)?.toUpperCase() || "M",
          date: msg.date || "",
          time: msg.time || "",
          created_at: msg.created_at,
          formattedTime: moment
            .utc(msg.created_at)
            .utcOffset("+05:30")
            .format("DD MMM YYYY, hh:mm A"),
          hasAttachment: !!msg.attached_file,
          originalFile: msg.original_file || "",
          attachedFile: msg.attached_file || "",
        }));

        setMessages(formatted);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error("Error fetching sent messages:", err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageDetail = async (messageId) => {
    try {
      setDetailLoading(true);
      const response = await AxiosInstance.get(
        `/message/getSentItem/${messageId}`,
      );

      if (
        response.data?.success &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        const detail = response.data.data[0];
        setSelectedMessage({
          id: detail.id,
          subject: detail.subject || "",
          body: detail.messages || "",
          senderName: detail.name || "",
          attached_file: detail.attached_file || null,
          original_file: detail.original_file || null,
          formattedTime: moment
            .utc(detail.created_at || "")
            .utcOffset("+05:30")
            .format("DD MMM YYYY, hh:mm A"),
        });
      }
    } catch (err) {
      console.error("Error fetching message detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessageId(message.id);
    // Set basic info immediately from list, then fetch full detail
    setSelectedMessage({
      id: message.id,
      subject: message.subject,
      body: message.body,
      senderName: "",
      attached_file: message.attachedFile || null,
      original_file: message.originalFile || null,
      formattedTime: message.formattedTime,
    });
    fetchMessageDetail(message.id);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    fetchSentMessages(value);
  };

  return (
    <div className={styles.inboxContainer}>
      {/* LEFT PANEL */}
      <div className={styles.messageList}>
        {/* Search */}
        <div className={styles.searchWrapper}>
          <div className="col-lg-12 px-2 col-md-12 col-sm-12">
            <CustomTextInput
              name="search"
              type="text"
              placeholder={
                translations.labels?.searchMessages || "Search messages..."
              }
              value={searchText}
              onChange={handleSearch}
              icon={<i className="fas fa-search"></i>}
              iconPosition="right"
            />
          </div>
        </div>

        {loading ? (
          <div className={styles.emptyState}>
            <p>{translations.labels?.loading || "Loading..."}</p>
          </div>
        ) : messages.length > 0 ? (
          messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.messageItem} ${
                selectedMessageId === message.id ? styles.selected : ""
              }`}
              onClick={() => handleSelectMessage(message)}
            >
              <div className={styles.messageHeader}>
                <div className={styles.avatar}>{message.senderInitial}</div>

                <div className={styles.messageInfo}>
                  <div className={styles.messageTitleRow}>
                    <h4 className={styles.messageTitle}>{message.subject}</h4>
                    <span className={styles.messageTime}>
                      {message.date}/{message.time}
                    </span>
                  </div>
                  <p className={styles.messageSender}>
                    {message.body?.length > 30
                      ? `${message.body.substring(0, 30)}...`
                      : message.body}
                  </p>
                  {message.hasAttachment && (
                    <span className={styles.attachmentIcon}>📎</span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>
              {translations.labels?.noSentMessagesFound ||
                "No sent messages found"}
            </p>
          </div>
        )}
      </div>

      {/* RIGHT PANEL */}
      <div className={styles.messageDetailPanel}>
        {selectedMessage ? (
          detailLoading ? (
            <div className={styles.emptyMessageDetail}>
              <p>{translations.labels?.loading || "Loading..."}</p>
            </div>
          ) : (
            <div className={styles.messageDetail}>
              <div className={styles.messageDetailHeader}>
                <div>
                  <h3 className={styles.messageSubject}>
                    {selectedMessage.subject}
                  </h3>
                  {/* {selectedMessage.senderName && (
                    <span className={styles.senderName}>
                      {selectedMessage.senderName}
                    </span>
                  )} */}
                </div>
                {/* <span className={styles.messageDetailTime}>
                  {selectedMessage.formattedTime}
                </span> */}
              </div>

              <div className={styles.messageBody}>
                <p>{selectedMessage.body}</p>
              </div>

              {selectedMessage.attached_file && (
                <div className={styles.attachmentWrapper}>
                  {/* <p className={styles.attachmentLabel}>
                    📎 {selectedMessage.original_file || "Attachment"}
                  </p> */}
                  <BlobImage
                    path={selectedMessage.attached_file}
                    alt={selectedMessage.original_file || "Attachment"}
                    className={styles.attachmentImage}
                  />
                </div>
              )}
            </div>
          )
        ) : (
          <div className={styles.emptyMessageDetail}>
            <p>
              {translations.labels?.selectMessageToViewDetails ||
                "Select a message to view details"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendItems;